import React, { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { CreditCard, Loader2, AlertTriangle, CheckCircle2, Clock, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import TopBar from '../components/layout/TopBar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { PayerForm } from '../components/billing/PayerForm';
import { CauraCheckout } from '../components/billing/CauraCheckout';

const STAGE_STYLES = {
  al_dia: { label: 'Al día', className: 'bg-[#1F9D7A]/10 text-[#1F9D7A] border-[#1F9D7A]/20', Icon: CheckCircle2 },
  en_mora: { label: 'Pago pendiente', className: 'bg-[#C58B2A]/10 text-[#C58B2A] border-[#C58B2A]/20', Icon: Clock },
  suspendida: { label: 'Suite bloqueada', className: 'bg-destructive/10 text-destructive border-destructive/20', Icon: ShieldAlert },
  restringida: { label: 'App restringida', className: 'bg-destructive/10 text-destructive border-destructive/20', Icon: ShieldAlert },
};

export default function BillingPage() {
  const { setMobileMenuOpen, churchInfo } = useOutletContext();
  const { selectedChurchId } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/api/billing/state')
      .then(res => setState(res.data))
      .catch(() => setState(null))
      .finally(() => setLoading(false));
  }, [selectedChurchId]);

  useEffect(() => { load(); }, [load]);

  const pendingInvoice = state?.invoices?.find(i => i.status === 'pending');

  const startPayment = async (invoiceId) => {
    setCreating(true);
    try {
      const res = await api.post(`/api/billing/invoices/${invoiceId}/order`, {});
      setOrder(res.data.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo iniciar el pago');
    } finally {
      setCreating(false);
    }
  };

  const emitInvoice = async () => {
    setCreating(true);
    try {
      await api.post('/api/billing/invoices/ensure', null, { params: { church_id: state?.church_id } });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo emitir la factura');
    } finally {
      setCreating(false);
    }
  };

  const stage = STAGE_STYLES[state?.stage] || STAGE_STYLES.al_dia;
  const StageIcon = stage.Icon;

  return (
    <div data-testid="billing-page">
      <TopBar title="Suscripción y pagos" onMenuClick={() => setMobileMenuOpen(true)} churchInfo={churchInfo} />
      <div className="px-4 sm:px-6 lg:px-8 py-5 max-w-4xl space-y-5">
        {loading ? (
          <Skeleton className="h-40 w-full" />
        ) : !state?.church_id ? (
          <Card><CardContent className="p-6 text-sm text-muted-foreground">
            Selecciona una congregación para ver su suscripción.
          </CardContent></Card>
        ) : !state.pays_base ? (
          <Card data-testid="billing-free-plan">
            <CardContent className="p-6">
              <Badge variant="outline" className="bg-[#1F9D7A]/10 text-[#1F9D7A] border-[#1F9D7A]/20 mb-3">
                {state.plan_label}
              </Badge>
              <p className="text-sm text-muted-foreground">
                Esta congregación no paga suscripción: forma parte del padrón fundador.
                {state.members != null && ` Hoy tiene ${state.members} miembros aprobados.`}
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                      {state.plan_label}
                    </p>
                    <p className="text-2xl font-semibold" data-testid="billing-amount">
                      {state.price?.monthly?.toFixed(2)} USD
                      <span className="text-sm font-normal text-muted-foreground"> /mes</span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {state.members} miembros aprobados · {state.price?.detail}
                    </p>
                  </div>
                  <Badge variant="outline" className={`${stage.className} flex items-center gap-1`}
                         data-testid="billing-stage">
                    <StageIcon className="w-3.5 h-3.5" />
                    {stage.label}
                  </Badge>
                </div>

                {state.in_trial && (
                  <div className="mt-4 rounded-lg bg-[#2B7DBA]/[0.06] border border-[#2B7DBA]/15 p-3"
                       data-testid="billing-trial">
                    <p className="text-sm text-foreground">
                      Mes de cortesía hasta el{' '}
                      <strong>{new Date(state.trial_until).toLocaleDateString('es-VE')}</strong>.
                      La primera factura se emite ese día.
                    </p>
                  </div>
                )}

                {state.days_overdue > 0 && (
                  <div className="mt-4 rounded-lg bg-destructive/[0.06] border border-destructive/20 p-3"
                       data-testid="billing-overdue-alert">
                    <p className="text-sm flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
                      <span>
                        <strong>{state.days_overdue} día(s) de atraso.</strong>{' '}
                        {state.stage === 'en_mora' &&
                          `La Suite se bloquea en ${state.suspends_in_days} día(s) si no se registra el pago.`}
                        {state.stage === 'suspendida' &&
                          'La Suite está bloqueada. A los 30 días se limita también el acceso de los miembros en la app.'}
                        {state.stage === 'restringida' &&
                          'El acceso de los miembros en la app está limitado hasta regularizar el pago.'}
                      </span>
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            <PayerForm payer={state.payer} churchId={state.church_id} onSaved={load} />

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base md:text-lg flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-[#056A84]" /> Facturas
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {state.invoices.length === 0 ? (
                  <div className="text-sm text-muted-foreground">
                    Todavía no hay facturas emitidas.
                    {!state.in_trial && (
                      <Button variant="outline" size="sm" className="ml-3" onClick={emitInvoice}
                              disabled={creating} data-testid="emit-invoice-btn">
                        {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Emitir la de este mes'}
                      </Button>
                    )}
                  </div>
                ) : state.invoices.map(inv => (
                  <div key={inv.id}
                       className="flex items-center justify-between gap-3 p-3 rounded-lg border"
                       data-testid={`invoice-row-${inv.id}`}>
                    <div>
                      <p className="text-sm font-medium">
                        {new Date(inv.period_start).toLocaleDateString('es-VE', { month: 'long', year: 'numeric' })}
                        {' · '}{inv.amount_usd.toFixed(2)} USD
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Vence {new Date(inv.due_date).toLocaleDateString('es-VE')}
                        {inv.members != null && ` · ${inv.members} miembros`}
                      </p>
                    </div>
                    {inv.status === 'paid' ? (
                      <Badge variant="outline" className="bg-[#1F9D7A]/10 text-[#1F9D7A] border-[#1F9D7A]/20">
                        Pagada
                      </Badge>
                    ) : (
                      <Button size="sm" className="bg-[#056A84] hover:bg-[#045A70] text-white"
                              onClick={() => startPayment(inv.id)} disabled={creating || !state.payer}
                              data-testid={`pay-invoice-${inv.id}`}>
                        {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Pagar'}
                      </Button>
                    )}
                  </div>
                ))}
                {!state.payer && pendingInvoice && (
                  <p className="text-xs text-muted-foreground">
                    Completa primero los datos de quien paga.
                  </p>
                )}
              </CardContent>
            </Card>

            {order && (
              <CauraCheckout order={order} onClose={() => { setOrder(null); load(); }} onPaid={load} />
            )}
          </>
        )}
      </div>
    </div>
  );
}
